"use client";
import { Track } from "@/src/lib/tracking";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useParams } from "next/navigation";
import type {
  PlaidLinkOnEventMetadata,
  PlaidLinkOnExitMetadata,
} from "react-plaid-link";
import type { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";

import { PlaidLinkWebSession } from "./PlaidLinkWebSession";
import { isPlaidUserCanceledError } from "./plaidFlowErrors";
import { isPlaidOAuthRedirect } from "./plaidOAuth";
import {
  type FetchLinkTokenParams,
  useCreateLinkToken,
} from "./useCreateLinkToken";
import type { PlaidMemberAuthorizationSuccessOutcome } from "./memberPlaidApi";
import {
  type PlaidLinkExitError,
  useExchangeAccessToken,
} from "./useExchangeAccessToken";
import { isPlaidNativeLinkRoute } from "./plaidPlatform";
import { openPlaidLinkInApp } from "@/src/lib/bridge/plaidLink";
import {
  trackPlaidLinkExit,
  trackPlaidLinkOpen,
  trackPlaidLinkStep,
  trackPlaidLinkSuccess,
  type PlaidLinkOpenTrigger,
  type PlaidLinkTrackingContext,
} from "./plaidLinkTracking";
import { useMemoizedFn } from "ahooks";

export interface OpenPlaidLinkParams {
  /** Plaid update mode — existing connection internal ID */
  connectionId?: string;
  /** 指定具体 Broker 直连 */
  brokerId?: number;
  /** 授权过期重新连接时的 account_id (仅 update mode 需要) */
  accountId?: string;
  /** @deprecated 请使用 accountId */
  institutionId?: string;
}

export interface UsePlaidLinkFlowOptions {
  autoInit?: boolean;
  onBindSuccess?: (
    outcome: PlaidMemberAuthorizationSuccessOutcome,
  ) => void | Promise<void>;
  onBindError?: (error: unknown) => void | Promise<void>;
  /** User left Plaid Link without bind (web onExit null/user-cancel, native cancel). */
  onLinkExit?: () => void | Promise<void>;
  /** Authorization returned INSTITUTION_ALREADY_LINKED (146114) with update link_token. */
  onInstitutionAlreadyLinked?: (
    updateLinkToken: string,
  ) => void | Promise<void>;
  /**
   * 调用方补充的业务维度，原样透传给四条 `plaid_link_*` 事件。
   * hook 自己不认识这些字段（它只管 Plaid，不管谁调它）——
   * 例如 financial-analysis-entry 传 surface/board/entry/account_target。
   */
  trackingContext?: PlaidLinkTrackingContext;
  /**
   * 业务入口追踪参（V3.0.8 roast handoff 加参；缺省不出键，既有调用方零改动）。
   * plaid_link_open/step/exit → `entry=`、plaid_link_success → `source=`
   * （与 native PlaidLinkFlow.kt 逐字段对齐）。
   */
  entry?: string;
}

interface WebPlaidSessionState {
  linkToken: string;
  sessionKey: number;
  autoOpen: boolean;
  receivedRedirectUri?: string;
}

/**
 * 本次打开的埋点维度 —— success / exit 要和它配对的那条 open 带同样的 trigger/is_update_mode，
 * 否则「rebind 的成功率」这类问题在数据里拆不出来。存 ref 而不是 state：
 * 它只被埋点读，参与渲染只会白白多一次 re-render。
 */
interface PlaidLinkAttempt {
  mode: "web" | "in_app";
  isUpdateMode: boolean;
  /** 业务入口追踪参（本次打开的快照，success/exit 与配对 open 带同一值）。 */
  entry?: string;
}

export interface UsePlaidLinkFlowResult {
  /** 按钮转圈：仅 link token 拉取 + 打开 Plaid（不含 bind） */
  loading: boolean;
  /** 绑定中：禁用按钮但不显示转圈，避免整页「假死」观感 */
  binding: boolean;
  linkToken: string | null;
  tokenError: string | null;
  openLink: (params?: OpenPlaidLinkParams) => Promise<void>;
  /** Open Plaid with an existing link_token (e.g. 146114 update token). Skips createLinkToken. */
  openLinkWithToken: (linkToken: string) => Promise<void>;
  /** Web/desktop: render once in the consumer tree so PlaidLinkWebSession can mount. */
  webLinkHost: ReactNode;
}

const toLinkExitError = (
  error: {
    error_type?: string;
    error_code?: string;
    error_message?: string;
    display_message?: string;
  } | null,
  metadata?: PlaidLinkOnExitMetadata,
): PlaidLinkExitError => ({
  error_type: error?.error_type ?? "",
  error_code: error?.error_code ?? "",
  error_message: error?.error_message ?? "",
  display_message: error?.display_message ?? "",
  institution_name: metadata?.institution?.name ?? "",
});

/** SDK exit metadata → 埋点维度（字段名对齐 Plaid 文档，便于与 `plaid_link_step` 同口径 join）。 */
const toExitTrackingMetadata = (
  error: {
    error_type?: string;
    error_code?: string;
    error_message?: string;
    display_message?: string;
  } | null,
  metadata?: PlaidLinkOnExitMetadata,
) => ({
  institution_id: metadata?.institution?.institution_id,
  institution_name: metadata?.institution?.name,
  error_code: error?.error_code,
  error_type: error?.error_type,
  error_message: error?.error_message,
  exit_status: metadata?.status,
  request_id: metadata?.request_id,
  link_session_id: metadata?.link_session_id,
});

export const usePlaidLinkFlow = (
  options: UsePlaidLinkFlowOptions = {},
): UsePlaidLinkFlowResult => {
  const {
    autoInit = false,
    onBindSuccess,
    onBindError,
    onLinkExit,
    onInstitutionAlreadyLinked,
    trackingContext,
    entry,
  } = options;
  const { device } = useParams<{ device?: DeviceType }>();
  const isInApp = isPlaidNativeLinkRoute(device);
  const [opening, setOpening] = useState(false);
  const [webSession, setWebSession] = useState<WebPlaidSessionState | null>(
    null,
  );
  const sessionKeyRef = useRef(0);
  const openGenerationRef = useRef(0);
  const activeLinkTokenRef = useRef<string | null>(null);
  const authorizationAttemptedRef = useRef(false);
  const oauthSessionStartedRef = useRef(false);
  const attemptRef = useRef<PlaidLinkAttempt>({
    mode: "web",
    isUpdateMode: false,
  });

  const {
    linkToken,
    loading: tokenLoading,
    error: tokenErrorState,
    createLinkToken,
  } = useCreateLinkToken({ autoInit });

  const {
    finalizePlaidAuthorization,
    reportLinkExitError,
    loading: authorizationLoading,
    error: authorizationError,
  } = useExchangeAccessToken();

  const clearWebSession = useMemoizedFn(() => {
    setWebSession(null);
  });

  const completeLinkSession = useMemoizedFn(async (notifyError = true) => {
    authorizationAttemptedRef.current = true;
    try {
      const activeLinkToken = activeLinkTokenRef.current ?? linkToken;
      if (!activeLinkToken) {
        throw new Error("Missing link token for authorization");
      }
      const outcome = await finalizePlaidAuthorization(activeLinkToken);
      if (!outcome) {
        throw new Error(authorizationError ?? "Plaid authorization failed");
      }

      if (outcome.kind === "institution_already_linked") {
        await onInstitutionAlreadyLinked?.(outcome.updateLinkToken);
        return true;
      }

      if (!outcome.connectionId) {
        throw new Error(authorizationError ?? "Plaid authorization failed");
      }

      // 与 open/exit 同层发射 —— 分子分母同源，`success / open` 才是真实的第三方授权成功率。
      // （原来只有 financial-analysis-entry 一个入口发，被其它入口的 open 稀释。）
      trackPlaidLinkSuccess({
        mode: attemptRef.current.mode,
        isUpdateMode: attemptRef.current.isUpdateMode,
        context: trackingContext,
        entry: attemptRef.current.entry,
      });
      await onBindSuccess?.(outcome);
      return true;
    } catch (error) {
      Track.error(error, {
        tag: "usePlaidLinkFlow",
        msg: "[completeLinkSession] bind failed",
      });
      if (notifyError) {
        await onBindError?.(error);
      }
      return false;
    } finally {
      clearWebSession();
      setOpening(false);
    }
  });

  const onPlaidLinkCompleted = useMemoizedFn(async () => {
    await completeLinkSession();
  });

  const onPlaidExit = useMemoizedFn(
    async (
      error: {
        error_type?: string;
        error_code?: string;
        error_message?: string;
        display_message?: string;
      } | null,
      metadata: PlaidLinkOnExitMetadata,
    ) => {
      setOpening(false);
      clearWebSession();
      const isUserCancel = error == null || isPlaidUserCanceledError(error);
      trackPlaidLinkExit({
        mode: "web",
        isUserCancel,
        metadata: toExitTrackingMetadata(error, metadata),
        context: trackingContext,
        entry: attemptRef.current.entry,
      });
      if (isUserCancel) {
        await onLinkExit?.();
        return;
      }
      // onSuccess can be lost on WebView redirect; backfill authorization once via onExit (idempotent).
      if (!authorizationAttemptedRef.current) {
        Track.log(
          "warn",
          "usePlaidLinkFlow",
          "[onPlaidExit] backfill authorization",
          {},
        );
        const authorized = await completeLinkSession(false);
        if (authorized) {
          return;
        }
      }
      await reportLinkExitError(toLinkExitError(error, metadata));
      Track.log("error", "usePlaidLinkFlow", "[onPlaidExit] bind failed", {
        arg1: String(error),
        arg2: String(metadata),
      });

      await onBindError?.(error);
    },
  );

  /**
   * App WebView 分支：把 link_token 交给原生壳的 `plaid.openLink`。
   *
   * 埋点上与 web 分支的差别是**只有两点**（open / exit）—— Plaid SDK 跑在壳里，
   * 它的 `onEvent` 流不过 JS，所以 `plaid_link_step` 在这条路径上永远没有。
   */
  const openInAppLink = useMemoizedFn(
    async (
      token: string,
      trigger: PlaidLinkOpenTrigger,
      isUpdateMode: boolean,
    ): Promise<void> => {
      try {
        activeLinkTokenRef.current = token;
        attemptRef.current = { mode: "in_app", isUpdateMode, entry };
        trackPlaidLinkOpen({
          mode: "in_app",
          trigger,
          isUpdateMode,
          context: trackingContext,
          entry,
        });
        await openPlaidLinkInApp({ linkToken: token });
        await completeLinkSession();
      } catch (error) {
        const isUserCancel = isPlaidUserCanceledError(error);
        trackPlaidLinkExit({
          mode: "in_app",
          isUserCancel,
          metadata: { error_message: String(error) },
          context: trackingContext,
          entry: attemptRef.current.entry,
        });
        if (isUserCancel) {
          await onLinkExit?.();
          return;
        }
        Track.error(error, {
          tag: "usePlaidLinkFlow",
          msg: "[openInAppLink] bind failed",
        });
        await onBindError?.(error);
      } finally {
        setOpening(false);
      }
    },
  );

  const mountWebSession = useMemoizedFn(
    (token: string, autoOpen: boolean, receivedRedirectUri?: string): void => {
      activeLinkTokenRef.current = token;
      sessionKeyRef.current += 1;
      setWebSession({
        linkToken: token,
        sessionKey: sessionKeyRef.current,
        autoOpen,
        receivedRedirectUri,
      });
    },
  );

  useEffect(() => {
    if (isInApp || !linkToken || oauthSessionStartedRef.current) {
      return;
    }
    if (!isPlaidOAuthRedirect()) {
      return;
    }
    oauthSessionStartedRef.current = true;
    authorizationAttemptedRef.current = false;
    setOpening(true);
    // 从银行 OAuth 页回跳后续跑同一个 link_token —— 与用户主动点开必须在数据里分开，
    // 否则 open 数会把一次连接算成两次。
    attemptRef.current = { mode: "web", isUpdateMode: false, entry };
    trackPlaidLinkOpen({
      mode: "web",
      trigger: "oauth_resume",
      isUpdateMode: false,
      context: trackingContext,
      entry,
    });
    mountWebSession(
      linkToken,
      true,
      typeof window !== "undefined" ? window.location.href : undefined,
    );
    // trackingContext 进依赖是为了取到最新值；重复执行由 oauthSessionStartedRef 挡着，
    // 调用方每次渲染传新对象也不会多发一条 open。
  }, [isInApp, linkToken, mountWebSession, trackingContext, entry]);

  const openLinkWithToken = useMemoizedFn(async (token: string) => {
    authorizationAttemptedRef.current = false;
    setOpening(true);
    const generation = ++openGenerationRef.current;

    try {
      if (!isInApp) {
        clearWebSession();
      }

      if (generation !== openGenerationRef.current) {
        return;
      }

      if (isInApp) {
        await openInAppLink(token, "rebind", true);
        return;
      }

      // rebind：146114 返回的 update-mode token 重开，不是首次绑定
      attemptRef.current = { mode: "web", isUpdateMode: true, entry };
      trackPlaidLinkOpen({
        mode: "web",
        trigger: "rebind",
        isUpdateMode: true,
        context: trackingContext,
        entry,
      });
      mountWebSession(token, true);
    } catch (error) {
      if (generation !== openGenerationRef.current) {
        return;
      }
      if (isPlaidUserCanceledError(error)) {
        setOpening(false);
        return;
      }
      Track.error(error, {
        tag: "usePlaidLinkFlow",
        msg: "[openLinkWithToken] bind failed",
      });
      await onBindError?.(error);
      setOpening(false);
    }
  });

  const openLink = useMemoizedFn(async (params?: OpenPlaidLinkParams) => {
    authorizationAttemptedRef.current = false;
    setOpening(true);
    const generation = ++openGenerationRef.current;
    const fetchParams: FetchLinkTokenParams | undefined = params
      ? {
          connectionId: params.connectionId,
          brokerId: params.brokerId,
          accountId: params.accountId ?? params.institutionId,
        }
      : undefined;

    try {
      if (!isInApp) {
        clearWebSession();
      }

      const token = await createLinkToken(fetchParams);
      if (generation !== openGenerationRef.current) {
        return;
      }

      if (!token) {
        throw new Error(
          tokenErrorState?.error_message ?? "Failed to create link token",
        );
      }

      const isUpdateMode = Boolean(params?.connectionId);

      if (isInApp) {
        await openInAppLink(token, "user", isUpdateMode);
        return;
      }

      attemptRef.current = { mode: "web", isUpdateMode, entry };
      trackPlaidLinkOpen({
        mode: "web",
        trigger: "user",
        isUpdateMode,
        context: trackingContext,
        entry,
      });
      mountWebSession(token, true);
    } catch (error) {
      if (generation !== openGenerationRef.current) {
        return;
      }
      if (isPlaidUserCanceledError(error)) {
        setOpening(false);
        return;
      }
      Track.error(error, {
        tag: "usePlaidLinkFlow",
        msg: "[openLink] bind failed",
      });
      await onBindError?.(error);
      setOpening(false);
    }
  });

  const handleWebOpened = useMemoizedFn(() => {
    setOpening(false);
  });

  /**
   * Plaid SDK 事件流 → `plaid_link_step`。
   *
   * 这是「用户在 Plaid 里走到哪一步」的唯一来源：`onSuccess`/`onExit` 只有终局，
   * 中间的选机构 / 跳 OAuth / 提交凭据 / 报错全在这条上。
   */
  const handleSdkEvent = useMemoizedFn(
    (eventName: string, metadata: PlaidLinkOnEventMetadata) => {
      trackPlaidLinkStep(
        eventName,
        {
          view_name: metadata.view_name,
          institution_id: metadata.institution_id,
          institution_name: metadata.institution_name,
          error_code: metadata.error_code,
          error_type: metadata.error_type,
          error_message: metadata.error_message,
          exit_status: metadata.exit_status,
          mfa_type: metadata.mfa_type,
          request_id: metadata.request_id,
          link_session_id: metadata.link_session_id,
        },
        trackingContext,
        entry,
      );
    },
  );

  const webLinkHost = useMemo(() => {
    if (isInApp || !webSession) {
      return null;
    }
    return (
      <PlaidLinkWebSession
        key={`${webSession.linkToken}-${webSession.sessionKey}`}
        linkToken={webSession.linkToken}
        autoOpen={webSession.autoOpen}
        receivedRedirectUri={webSession.receivedRedirectUri}
        onLinkCompleted={onPlaidLinkCompleted}
        onExit={onPlaidExit}
        onOpened={handleWebOpened}
        onSdkEvent={handleSdkEvent}
      />
    );
  }, [
    handleSdkEvent,
    handleWebOpened,
    isInApp,
    onPlaidExit,
    onPlaidLinkCompleted,
    webSession,
  ]);

  const tokenError =
    tokenErrorState?.error_message ?? authorizationError ?? null;
  const openingLoading = tokenLoading || opening;
  const binding = authorizationLoading;

  return {
    loading: openingLoading,
    binding,
    linkToken,
    tokenError,
    openLink,
    openLinkWithToken,
    webLinkHost,
  };
};
